/**
 * The record on every task row (Tasks Both Ways slice 2, 10/28 —
 * https://claude.ai/artifact/H7gsi8ERjULUpGCpUMZzH8 "The record on every row").
 *
 * One small chip: an icon for what it is (job, device, customer, place,
 * paperwork) and its plain name. One tap opens that record. It sits inside
 * clickable rows, so it stops the row's own click from also opening the task.
 */
import { Link } from "react-router-dom";
import { Briefcase, Building2, FileText, Gauge, MapPin, Package, Receipt } from "lucide-react";
import { cn } from "@/lib/utils";
import type { TaskRecord } from "@/lib/tasks";

const ICONS: Record<string, typeof Briefcase> = {
    job: Briefcase,
    asset: Gauge,
    customer: Building2,
    site: MapPin,
    purchase_order: Package,
    invoice: Receipt,
    estimate: FileText,
    wo_intake_item: FileText,
};

/** What the chip says to a screen reader. Pure, exported for tests. */
export function recordChipLabel(record: Pick<TaskRecord, "entityType" | "label">): string {
    return `Open ${record.label}`;
}

export function TaskRecordChip({
    record,
    size = "sm",
    className,
}: {
    record: TaskRecord | null | undefined;
    /** "lg" is the iPad's big button. */
    size?: "sm" | "lg";
    className?: string;
}) {
    if (!record) return null;
    const Icon = ICONS[record.entityType] ?? FileText;
    return (
        <Link
            to={record.href}
            data-testid="task-record-chip"
            aria-label={recordChipLabel(record)}
            title={recordChipLabel(record)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            className={cn(
                "inline-flex max-w-full items-center gap-1.5 rounded-md border border-border bg-background font-semibold text-foreground hover:border-primary hover:bg-primary/5",
                size === "lg" ? "min-h-11 px-3 text-base" : "px-2 py-0.5 text-xs",
                className,
            )}
        >
            <Icon className={cn("shrink-0 text-muted-foreground", size === "lg" ? "h-5 w-5" : "h-3.5 w-3.5")} />
            <span className="truncate">{record.label}</span>
        </Link>
    );
}
